import type { BillingHistoryDoc } from "../Services/Billing";
import { getFirmById } from "../Services/Firm";
import type { AccountingOfficeDetail } from "./accountingOfficeDetail";
import { officeLegalName, toFeeContext } from "./accountingOfficeDetail";
import { resolveAccountingOfficeContacts } from "./accountingContactInheritance";
import {
  matchProfessionalsRange,
  parseProfessionalsCount,
} from "./accountingFeesCalculations";
import { deriveBillingStatus } from "./billingStatus";
import { fiscalYearFromIso, parseCalendarDate } from "./dates";
import {
  downloadInvoicePDF,
  generateInvoicePDFWithLogo,
  type InvoiceCoverLetter,
} from "./generateAccountingInvoicePDFTemplate";
import { getAccountingMailingAddressForInvoice } from "./firmInvoiceAddress";
import type { IFirmAddress } from "../interfaces/models/IFirm";
import { MEMBER_BALANCE_CREDIT_LABEL } from "../pages/Accounting/accountingModalUi";
import { getJurisdictionLabel } from "./jurisdiction/structuredJurisdiction";
import { resolveCoverLetterBodyParagraphs } from "./accountingCoverLetter";
import {
  BILLING_CATEGORY_LABELS,
  type BillingCategory,
} from "./billingCategoryUtils";

/** Italic terms shown inside the invoice box, above the TOTAL row. */
const INVOICE_BOX_DISCLAIMER_LINES = [
  "Annual fees are due by the first month of membership renewal.",
  "A late fee of 10% annually will be applied on a monthly basis until receipt of total fees.",
];

const INVOICE_KIND_LABELS: Record<string, string> = {
  membershipDues: "Membership Dues",
  assessment: "Assessment",
  special: "Assessment",
  annualMeeting: "Annual Meeting",
  lateFee: "Late Fee",
  contingency: "Contingency",
  event: "Event",
  other: "Other",
};

export type AccountingInvoicePdfInput = {
  invoice: BillingHistoryDoc;
  office: AccountingOfficeDetail;
  /** Unsaved memo from the preview modal; falls back to invoice.memo. */
  memoOverride?: string;
  /** Overrides the "Amount Paid" label (e.g. credit balance source). */
  paidLabel?: string;
};

function formatInvoicePdfDate(iso: string): string {
  const trimmed = iso.trim();
  if (!trimmed) {
    return "";
  }
  const parsed = new Date(
    /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? `${trimmed}T12:00:00` : trimmed,
  );
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  return `${parsed.getDate()} ${parsed.toLocaleString("en-US", { month: "long" })} ${parsed.getFullYear()}`;
}

function formatCoverLetterDate(date: Date = new Date()): string {
  return `${date.toLocaleString("en-US", { month: "long" })} ${date.getDate()}, ${date.getFullYear()}`;
}

/** Address fields may arrive as numbers (e.g. zipCode); coerce before trimming. */
function toTrimmedString(value: unknown): string {
  if (value == null) {
    return "";
  }
  return String(value).trim();
}

function buildCoverLetterAddressLines(
  address: IFirmAddress | undefined,
): string[] {
  if (!address) {
    return [];
  }
  const lines: string[] = [];
  const address1 = toTrimmedString(address.address1);
  const address2 = toTrimmedString(address.address2);
  if (address1) {
    lines.push(address1);
  }
  if (address2) {
    lines.push(address2);
  }
  const city = toTrimmedString(address.city);
  const state = toTrimmedString(address.state);
  const zip = toTrimmedString(address.zipCode);
  const country = toTrimmedString(address.country);
  const cityLine = [city, [state, zip].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  const lastLine = [cityLine, country].filter(Boolean).join(", ");
  if (lastLine) {
    lines.push(lastLine);
  }
  return lines;
}

function resolveMembershipCategoryLabel(office: AccountingOfficeDetail): string {
  const count = parseProfessionalsCount(toFeeContext(office));
  if (count === null) {
    return "";
  }
  const range = matchProfessionalsRange(count);
  return range ? `${range} Professionals` : "";
}

/**
 * Firm identity rows shown above the invoice charges (description-only, no amount).
 */
function buildFirmInfoLineItems(
  office: AccountingOfficeDetail,
  firmName: string,
): { description: string; amountText: string }[] {
  const items: { description: string; amountText: string }[] = [];
  const printedName = toTrimmedString(firmName);
  if (printedName) {
    items.push({ description: `Firm Name: ${printedName}`, amountText: "" });
  }
  const jurisdiction = toTrimmedString(getJurisdictionLabel(office));
  if (jurisdiction) {
    items.push({ description: `Jurisdiction: ${jurisdiction}`, amountText: "" });
  }
  const membershipCategory = resolveMembershipCategoryLabel(office);
  if (membershipCategory) {
    items.push({
      description: `Membership Category: ${membershipCategory}`,
      amountText: "",
    });
  }
  return items;
}


function buildSectionTitle(invoice: BillingHistoryDoc): string {
  const kind = invoice.context?.kind ?? "";
  const label = INVOICE_KIND_LABELS[kind] ?? (kind || "Invoice");
  const fy = invoice.context?.fiscalYear;
  return fy ? `FY${fy} ${label}` : label;
}

/** Section title used in cover letter "Re:" line and PDF headers. */
export function buildAccountingInvoiceSectionTitle(
  invoice: Pick<BillingHistoryDoc, "context">,
): string {
  return buildSectionTitle(invoice as BillingHistoryDoc);
}

const DUES_PERIOD_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

function formatDuesDate(date: Date): string {
  return `${date.getUTCDate()} ${DUES_PERIOD_MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

/**
 * Membership dues period for the invoice fiscal year (e.g. FY2027 /
 * billing period 2026-2027), from the firm's First Dues Date anniversary.
 */
function buildDuesPeriodSuffix(
  invoice: BillingHistoryDoc,
  office: AccountingOfficeDetail,
): string {
  const fiscalYear =
    invoice.context?.fiscalYear ??
    (invoice.issueDate ? fiscalYearFromIso(invoice.issueDate) : undefined);
  if (!fiscalYear || Number.isNaN(fiscalYear)) {
    return "";
  }

  const anniversary = parseCalendarDate(office.billingStartDate);
  const startMonth = anniversary ? anniversary.getUTCMonth() : 6;
  const startDay = anniversary ? anniversary.getUTCDate() : 1;

  const startDate = new Date(Date.UTC(fiscalYear - 1, startMonth, startDay));
  const endDate = new Date(Date.UTC(fiscalYear, startMonth, startDay));
  endDate.setUTCDate(endDate.getUTCDate() - 1);

  return ` from ${formatDuesDate(startDate)} to ${formatDuesDate(endDate)}`;
}

/** The three primary invoice concepts, rendered together above other charges. */
const MAIN_CONCEPT_CODES: readonly string[] = [
  "annual",
  "special",
  "annualMeeting",
];

/** Maps a billing category to the invoice concept code used for descriptions. */
const CATEGORY_TO_CONCEPT_CODE: Record<BillingCategory, string> = {
  annualDues: "annual",
  assessmentDues: "assessment",
  membershipBalance: "previousBalance",
  specialFund: "special",
  annualMeeting: "annualMeeting",
  lateFees: "late",
  initiation: "initiation",
};

/**
 * Maps a receipt line label (a billing category label) back to the invoice
 * concept code, so payment receipts reuse the same concept naming as invoices.
 */
const RECEIPT_LABEL_TO_CONCEPT_CODE: Record<string, string> = Object.fromEntries(
  (Object.keys(BILLING_CATEGORY_LABELS) as BillingCategory[]).map((cat) => [
    BILLING_CATEGORY_LABELS[cat],
    CATEGORY_TO_CONCEPT_CODE[cat],
  ]),
);

/** Capitalizes the first letter of each word, preserving the rest (e.g. "WSG"). */
function titleCaseConcept(text: string): string {
  return text.replace(
    /\S+/g,
    (word) => word.charAt(0).toUpperCase() + word.slice(1),
  );
}

function buildConceptDescription(
  item: { code?: string; description?: string },
  duesPeriodSuffix: string,
  chargeYear: number,
): string {
  const code = item.code ?? "";
  let base: string;
  if (code === "special") {
    base = "WSG Development Fund";
  } else if (code === "annualMeeting") {
    base = `Annual Meeting ${chargeYear} Registration - 1 Attendee`;
  } else if (code === "assessment") {
    base = `Assessment Fee ${chargeYear}`;
  } else {
    base = item.description?.trim() || item.code?.trim() || "Charge";
  }
  let description = titleCaseConcept(base);
  if (code === "annual" && duesPeriodSuffix) {
    description += duesPeriodSuffix;
  }
  return description;
}

function formatCurrencyAmount(amount: number, currency: string): string {
  const upper = currency.toUpperCase();
  const symbol =
    upper === "USD"
      ? "$ USD "
      : upper === "EUR"
        ? "€"
        : upper === "GBP"
          ? "£"
          : `${currency} `;
  const absAmount = Math.abs(amount);
  const formatted = absAmount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return amount < 0 ? `${symbol}-${formatted}` : `${symbol}${formatted}`;
}

function buildFilename(
  invoice: BillingHistoryDoc,
  officeName: string,
  paymentStatus: ReturnType<typeof deriveBillingStatus>,
): string {
  const safeOffice = officeName
    .replace(/[^a-zA-Z0-9\s-]/g, "")
    .replace(/\s+/g, "_")
    .slice(0, 40);
  const invoiceType = paymentStatus === "paid" ? "Paid_Invoice" : "Due_Invoice";
  const reference = (invoice.invoiceNumber ?? invoice.id)
    .replace(/[^a-zA-Z0-9_-]/g, "")
    .slice(0, 24);
  const issueDate = (invoice.issueDate ?? "").replace(/-/g, "");
  return `${invoiceType}_${safeOffice || "Office"}_${reference || "invoice"}${issueDate ? `_${issueDate}` : ""}.pdf`;
}

export async function generateAccountingInvoicePDFBlob({
  invoice,
  office,
  memoOverride,
  paidLabel,
}: AccountingInvoicePdfInput): Promise<Blob> {
  const total = Number(invoice.total) || 0;
  const totalPaid = Number(invoice.totalPaid) || 0;
  const creditApplied = Number(invoice.creditApplied) || 0;
  const resolvedPaidLabel =
    paidLabel?.trim() ||
    (creditApplied > 0 && creditApplied >= totalPaid
      ? MEMBER_BALANCE_CREDIT_LABEL
      : undefined);
  const paymentStatus = deriveBillingStatus(total, totalPaid);
  const currency = invoice.currency || "USD";
  const sectionTitle = buildSectionTitle(invoice);
  const memo = (memoOverride ?? invoice.memo ?? "").trim();

  const firmId = String(office.firm ?? "").trim();
  const firm = firmId ? await getFirmById(firmId).catch(() => null) : null;
  const officeName = officeLegalName(office, firm);
  const invoiceAddress = getAccountingMailingAddressForInvoice(
    firm ?? undefined,
  );

  const currentYear = new Date().getFullYear();
  const chargeYear =
    invoice.context?.fiscalYear ??
    (invoice.issueDate ? fiscalYearFromIso(invoice.issueDate) : currentYear);
  const duesPeriodSuffix = buildDuesPeriodSuffix(invoice, office);

  const mappedItems = (invoice.items ?? [])
    .map((item) => {
      const amount = (Number(item.amount) || 0) * (Number(item.quantity) || 1);
      if (amount === 0) {
        return null;
      }
      return {
        code: item.code ?? "",
        description: buildConceptDescription(
          item,
          duesPeriodSuffix,
          chargeYear,
        ),
        amountText: formatCurrencyAmount(amount, currency),
      };
    })
    .filter(
      (
        item,
      ): item is { code: string; description: string; amountText: string } =>
        Boolean(item),
    );

  const mainItems = mappedItems
    .filter((item) => MAIN_CONCEPT_CODES.includes(item.code))
    .map(({ description, amountText }) => ({ description, amountText }));
  const restItems = mappedItems
    .filter((item) => !MAIN_CONCEPT_CODES.includes(item.code))
    .map(({ description, amountText }) => ({ description, amountText }));

  // The template appends a credit/paid line after these items; it belongs to the
  // "rest" group, so reserve a separating blank line for it too.
  const willHaveTemplatePaidLine =
    (creditApplied > 0 && paymentStatus === "paid") ||
    (paymentStatus !== "paid" && totalPaid > 0);
  const hasRestGroup = restItems.length > 0 || willHaveTemplatePaidLine;

  const customLineItems: { description: string; amountText: string }[] =
    mainItems.length > 0 && hasRestGroup
      ? [...mainItems, { description: "", amountText: "" }, ...restItems]
      : [...mainItems, ...restItems];

  if (customLineItems.length === 0 && total > 0) {
    customLineItems.push({
      description: sectionTitle,
      amountText: formatCurrencyAmount(total, currency),
    });
  }

  const firmInfoLineItems = buildFirmInfoLineItems(office, officeName);
  const lineItemsWithFirmInfo =
    firmInfoLineItems.length > 0
      ? [
          { description: "", amountText: "" },
          ...firmInfoLineItems,
          { description: "", amountText: "" },
          ...customLineItems,
        ]
      : customLineItems;

  const contactResolution = await resolveAccountingOfficeContacts(office);
  const ccContactDisplayName =
    contactResolution.billingRecipients.ccName.trim() || undefined;
  const bodyParagraphs = resolveCoverLetterBodyParagraphs({
    savedBody: invoice.coverLetterBody,
    subject: sectionTitle,
    recipientName: officeName,
  });
  const coverLetter: InvoiceCoverLetter = {
    dateDisplay: formatCoverLetterDate(),
    recipientName: officeName,
    recipientAddressLines: buildCoverLetterAddressLines(invoiceAddress),
    subject: sectionTitle,
    greetingName: contactResolution.accountingContactName || undefined,
    bodyParagraphs,
    ccContactDisplayName,
  };

  const pdfBlob = await generateInvoicePDFWithLogo({
    event: {
      id: office.id,
      title: sectionTitle,
      fee: { currency },
    },
    person: { name: officeName },
    firm: { name: officeName },
    eventPayment: String(totalPaid),
    creditPayment: "0",
    paid: paymentStatus,
    paidLabel: resolvedPaidLabel,
    creditAppliedDisplay:
      creditApplied > 0 && paymentStatus === "paid" ? creditApplied : undefined,
    eventId: office.id,
    personId: office.id,
    invoiceAddress,
    customLineItems: lineItemsWithFirmInfo,
    customTotalAmount: total,
    referenceNumber: invoice.invoiceNumber,
    invoiceDateDisplay: invoice.issueDate
      ? formatInvoicePdfDate(invoice.issueDate)
      : undefined,
    regTerms: memo || undefined,
    notes: memo || undefined,
    hidePrepaidRefundNote: true,
    hideInvoiceSectionTitle: true,
    reminderCount: Number(invoice.timesSent) || 0,
    coverLetter,
    boxDisclaimerLines: INVOICE_BOX_DISCLAIMER_LINES,
  });

  if (!pdfBlob) {
    throw new Error("Failed to generate invoice PDF.");
  }

  return pdfBlob;
}

export type AccountingPaymentReceiptInput = {
  office: AccountingOfficeDetail;
  /** ISO date the payment was received/processed. */
  paymentDate: string;
  /** Invoice number or receipt reference to show in the header. */
  referenceNumber?: string;
  description?: string;
  /** One row per category allocation (or a single direct-payment row). */
  lineItems: { label: string; amount: number }[];
  totalAmount: number;
  currency?: string;
  /** Fiscal year of the paid invoice; drives the Annual Dues date range. */
  fiscalYear?: number;
};

export async function generateAccountingPaymentReceiptPDFBlob({
  office,
  paymentDate,
  referenceNumber,
  description,
  lineItems,
  totalAmount,
  currency = "USD",
  fiscalYear,
}: AccountingPaymentReceiptInput): Promise<Blob> {
  const sectionTitle = "Payment Receipt";
  const memo = (description ?? "").trim();

  const firmId = String(office.firm ?? "").trim();
  const firm = firmId ? await getFirmById(firmId).catch(() => null) : null;
  const officeName = officeLegalName(office, firm);
  const invoiceAddress = getAccountingMailingAddressForInvoice(
    firm ?? undefined,
  );

  const currentYear = new Date().getFullYear();
  const chargeYear =
    fiscalYear ??
    (paymentDate ? fiscalYearFromIso(paymentDate) : currentYear);
  const duesPeriodSuffix = buildDuesPeriodSuffix(
    {
      context: fiscalYear ? { fiscalYear } : undefined,
      issueDate: paymentDate,
    } as BillingHistoryDoc,
    office,
  );

  // Reuse the invoice concept naming for known categories; keep free-form
  // labels (e.g. direct payments) untouched.
  const mappedItems = lineItems
    .filter((item) => Number(item.amount) > 0)
    .map((item) => {
      const code = RECEIPT_LABEL_TO_CONCEPT_CODE[item.label.trim()] ?? "";
      const description = code
        ? buildConceptDescription(
            { code, description: item.label },
            duesPeriodSuffix,
            chargeYear,
          )
        : item.label;
      return {
        code,
        description,
        amountText: formatCurrencyAmount(Number(item.amount) || 0, currency),
      };
    });

  const mainItems = mappedItems
    .filter((item) => MAIN_CONCEPT_CODES.includes(item.code))
    .map(({ description, amountText }) => ({ description, amountText }));
  const restItems = mappedItems
    .filter((item) => !MAIN_CONCEPT_CODES.includes(item.code))
    .map(({ description, amountText }) => ({ description, amountText }));

  const customLineItems: { description: string; amountText: string }[] =
    mainItems.length > 0 && restItems.length > 0
      ? [...mainItems, { description: "", amountText: "" }, ...restItems]
      : [...mainItems, ...restItems];

  if (customLineItems.length === 0) {
    customLineItems.push({
      description: memo || "Payment",
      amountText: formatCurrencyAmount(totalAmount, currency),
    });
  }

  const firmInfoLineItems = buildFirmInfoLineItems(office, officeName);
  const lineItemsWithFirmInfo =
    firmInfoLineItems.length > 0
      ? [
          { description: "", amountText: "" },
          ...firmInfoLineItems,
          { description: "", amountText: "" },
          ...customLineItems,
        ]
      : customLineItems;

  const pdfBlob = await generateInvoicePDFWithLogo({
    event: {
      id: office.id,
      title: sectionTitle,
      fee: { currency },
    },
    person: { name: officeName },
    firm: { name: officeName },
    eventPayment: String(totalAmount),
    creditPayment: "0",
    paid: "paid",
    eventId: office.id,
    personId: office.id,
    invoiceAddress,
    customLineItems: lineItemsWithFirmInfo,
    customTotalAmount: totalAmount,
    invoiceSectionTitle: sectionTitle,
    referenceNumber: referenceNumber || undefined,
    invoiceDateDisplay: paymentDate
      ? formatInvoicePdfDate(paymentDate)
      : undefined,
    regTerms: memo || undefined,
    notes: memo || undefined,
    hidePrepaidRefundNote: true,
  });

  if (!pdfBlob) {
    throw new Error("Failed to generate payment receipt PDF.");
  }

  return pdfBlob;
}

export async function downloadAccountingInvoicePDF(
  invoice: BillingHistoryDoc,
  office: AccountingOfficeDetail,
): Promise<void> {
  const total = Number(invoice.total) || 0;
  const totalPaid = Number(invoice.totalPaid) || 0;
  const paymentStatus = deriveBillingStatus(total, totalPaid);
  const officeName = officeLegalName(office);
  const pdfBlob = await generateAccountingInvoicePDFBlob({ invoice, office });

  await downloadInvoicePDF(
    pdfBlob,
    buildFilename(invoice, officeName, paymentStatus),
  );
}

/**
 * Builds the receipt input for a stored payment doc. Allocation categories map
 * to line items; a direct payment falls back to a single description row.
 */
export function buildPaymentReceiptInputFromDoc(
  payment: BillingHistoryDoc,
  office: AccountingOfficeDetail,
  overrides?: { paymentDate?: string; description?: string },
): AccountingPaymentReceiptInput {
  const amount = Number(payment.amount) || 0;
  const description = (overrides?.description ?? payment.description ?? "").trim();
  const allocations = Array.isArray(payment.allocations)
    ? payment.allocations
    : [];
  const allocationLines = allocations
    .filter((a) => Number(a.amount) > 0)
    .map((a) => ({
      label: a.category?.trim() || "Payment",
      amount: Number(a.amount) || 0,
    }));
  const lineItems =
    allocationLines.length > 0
      ? allocationLines
      : amount > 0
        ? [{ label: description || "Payment", amount }]
        : [];
  return {
    office,
    paymentDate: overrides?.paymentDate ?? payment.paymentDate ?? "",
    referenceNumber: payment.reference ?? payment.invoiceNumber ?? undefined,
    description,
    lineItems,
    totalAmount: amount,
    currency: payment.currency ?? "USD",
    fiscalYear: payment.context?.fiscalYear,
  };
}

export async function downloadAccountingPaymentReceiptPDF(
  payment: BillingHistoryDoc,
  office: AccountingOfficeDetail,
): Promise<void> {
  const officeName = officeLegalName(office);
  const pdfBlob = await generateAccountingPaymentReceiptPDFBlob(
    buildPaymentReceiptInputFromDoc(payment, office),
  );
  const safeOffice =
    officeName
      .replace(/[^a-zA-Z0-9\s-]/g, "")
      .replace(/\s+/g, "_")
      .slice(0, 40) || "Office";
  const reference = (payment.reference ?? payment.id)
    .replace(/[^a-zA-Z0-9_-]/g, "")
    .slice(0, 24);
  const date = (payment.paymentDate ?? "").replace(/-/g, "");
  const filename = `Payment_Receipt_${safeOffice}_${reference || "payment"}${date ? `_${date}` : ""}.pdf`;

  await downloadInvoicePDF(pdfBlob, filename);
}
